import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme definitions
export const THEMES = {
    default: {
        name: 'Default',
        class: 'theme-default',
        colors: {
            bgPrimary: '#121314',
            bgSecondary: '#1f2021',
            bgTertiary: '#343536',
            borderPrimary: '#00daf3',
            borderSecondary: '#3b494c',
            textPrimary: '#e4e2e3',
            textSecondary: '#bac9cc',
            textMuted: '#849396',
            accent: '#00daf3',
            accentSecondary: '#00e5ff',
            success: '#10b981',
            warning: '#ffb700',
            danger: '#ef4444',
        }
    },
    dark: {
        name: 'Dark',
        class: 'theme-dark',
        colors: {
            bgPrimary: '#0d0d0d',
            bgSecondary: '#1a1a1a',
            bgTertiary: '#262626',
            borderPrimary: '#22d3ee',
            borderSecondary: '#333333',
            textPrimary: '#ffffff',
            textSecondary: '#a1a1aa',
            textMuted: '#71717a',
            accent: '#22d3ee',
            accentSecondary: '#0ea5e9',
            success: '#10b981',
            warning: '#f59e0b',
            danger: '#ef4444',
        }
    },
    light: {
        name: 'Light',
        class: 'theme-light',
        colors: {
            bgPrimary: '#7a9ba8',
            bgSecondary: '#b5ccd4',
            bgTertiary: '#e8e4dc',
            borderPrimary: '#4a7080',
            borderSecondary: '#9bb8c4',
            textPrimary: '#0f1f28',
            textSecondary: '#2a4450',
            textMuted: '#4a6878',
            accent: '#1a3d4d',
            accentSecondary: '#2a5060',
            success: '#1b5e40',
            warning: '#8a4000',
            danger: '#991b1b',
        }
    }
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(() => {
        // Get saved theme from localStorage or default to 'default'
        const savedTheme = localStorage.getItem('utms-theme');
        return savedTheme && THEMES[savedTheme] ? savedTheme : 'default';
    });

    useEffect(() => {
        // Save theme to localStorage
        localStorage.setItem('utms-theme', theme);
        
        // Apply theme class to document body and html
        document.documentElement.classList.remove('theme-default', 'theme-dark', 'theme-light');
        document.documentElement.classList.add(THEMES[theme].class);
        document.body.classList.remove('theme-default', 'theme-dark', 'theme-light');
        document.body.classList.add(THEMES[theme].class);
    }, [theme]);

    const cycleTheme = () => {
        const themeKeys = Object.keys(THEMES);
        const currentIndex = themeKeys.indexOf(theme);
        const nextIndex = (currentIndex + 1) % themeKeys.length;
        setTheme(themeKeys[nextIndex]);
    };

    return (
        <ThemeContext.Provider value={{ theme, setTheme, cycleTheme, currentTheme: THEMES[theme] }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}

export default ThemeContext;
